import { useNavigate, useParams } from 'react-router';

import { useEffect, useMemo, useState } from 'react';

import { useI18n } from '@app-velocity/i18n';

import {
  AppraisalContext,
  SinglePageReviews,
  useGetRemarksQuery,
} from '@app-velocity/modules/appraisals';

import { SimpleStatus } from '@app-velocity/common';

import { TNewAppraisalFlowRequest } from '@app-velocity/av-api-v2';

import { useGuestAccess } from '@app-velocity/config';

import { PageStatusHeader } from '../../../components/PageStatusHeader';
import { selectNode } from '../../../../core';
import { useReviewDetailsContext } from '../context/useReviewDetailsContext';
import { GuestNotice } from './components/GuestNotice';

import { useSavePlugin } from '../../../hooks';

export const ReviewDetails = () => {
  const { t } = useI18n();
  const [refetchAppraisal, setRefetchAppraisal] = useState(true);
  const navigate = useNavigate();

  const { handlePluginModal } = useSavePlugin();
  const { persistGuestState } = useGuestAccess();
  const { mode } = useReviewDetailsContext();

  const { reviewId, workspaceId } = useParams<{
    workspaceId: string;
    reviewId: string;
  }>();

  const { data: appraisal, isLoading } = useGetRemarksQuery(
    reviewId,
    workspaceId,
    mode,
    refetchAppraisal
  );

  const goBack = () => {
    navigate('..');
  };

  useEffect(() => {
    if (!appraisal) return;

    if (appraisal.appraisalStatus !== 'IN_PROGRESS') {
      setRefetchAppraisal(false);
    }

    if (mode === 'guest') {
      persistGuestState({
        type: 'review',
        workspaceId: workspaceId!,
        reviewId: reviewId!,
      });
    } else if (mode === 'authorized') {
      setTimeout(() => {
        handlePluginModal();
      }, 5000);
    }
  }, [appraisal]);

  // @TODO - temporary - needs tso have separate field for that!
  const getCategory = (): TNewAppraisalFlowRequest['method'] | null => {
    if (appraisal?.method) return appraisal.method;

    // fallback
    if (
      !appraisal ||
      !appraisal.nodeName ||
      !appraisal.nodeName.includes('--||--')
    )
      return null;
    const splitted = appraisal.nodeName.split('--||--');

    const [, category] = splitted;

    if (!category || !['accessibility', 'refactoringui'].includes(category))
      return null;

    if (category === 'accessibility') return 'ACCESSIBILITY';

    return 'REFACTORING_UI';
  };

  const getTitleByMethod = (): string => {
    const category = getCategory();

    switch (category) {
      case 'ACCESSIBILITY':
        return t('appraisals:design_review_accessibility', {
          level: appraisal?.level,
        });
      case 'REFACTORING_UI':
        return t('appraisals:design_review_refactoringui');
    }

    return 'appraisals:design_review';
  };

  const navigateToElement = (elementNodeId: string) => {
    selectNode(elementNodeId);
  };

  const status: SimpleStatus = useMemo(() => {
    if (isLoading) return 'pending';

    switch (appraisal?.appraisalStatus) {
      case 'FAILED':
        if (mode === 'guest') return 'paused';

        return 'error';
      case 'FINISHED':
        if (mode === 'guest') return 'paused';

        return 'success';
      case 'IN_PROGRESS':
        return 'pending';
      case 'NOT_STARTED':
        return 'pending';
    }

    return 'pending';
  }, [isLoading, appraisal]);

  return (
    <>
      <PageStatusHeader
        label={getTitleByMethod()}
        // @TODO - temporary
        description={appraisal?.nodeName?.split('--||--')[0] || ''}
        status={status}
        onBack={goBack}
      />
      <AppraisalContext.Provider
        value={{
          status,
          reviews: appraisal?.appraisals || null,
          image: appraisal?.nodeImage || '',
          nodeName: appraisal?.nodeName || '',
          nodeId: appraisal?.nodeId || '',
          category: getCategory(),
          navigateToElement,
        }}
      >
        <SinglePageReviews />
        <GuestNotice
          originalReviewsCount={appraisal?.hiddenRemarks || 0}
          filteredReviewsCount={appraisal?.appraisals?.length || 0}
          reviewStatus={status}
        />
      </AppraisalContext.Provider>
    </>
  );
};
